import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { formatUtcMonth } from '@/lib/format'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { ApiError } from '@/lib/api'
import { formatCurrency, formatMinutes, formatRate } from '@/lib/format'
import { CREDIT_TYPE_LABELS } from '../constants'
import { useAddBillingCreditMutation } from '../hooks/useAddBillingCreditMutation'
import { numberInput } from '../lib/numberInput'
import { currentUtcMonth } from '../lib/periods'
import { DayField } from './DayField'
import type {
  BillingCreditPlan,
  BillingCreditRequest,
  CreditEntryType,
} from '../types'

type CreditType = BillingCreditRequest['credit_type']
type CreditRequestBody = Omit<BillingCreditRequest, 'org_slug'>

const CREDIT_TYPE_ENTRY: Record<CreditType, CreditEntryType> = {
  purchase: 'credit_purchase',
  compensation: 'credit_compensation',
}

const MINUTES_PATTERN = /^\d+(\.\d{1,4})?$/
const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/
// Relay's rule: references the billing page reads back unchanged, three or
// more digits or letters and digits in hyphen-joined runs.
const INVOICE_NUMBER_PATTERN =
  /^(?=[A-Za-z0-9-]{3,}$)(?:\d+|(?=[A-Za-z0-9-]*[A-Za-z])(?=[A-Za-z0-9-]*\d)[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*)$/
/** How long after the last keystroke the dialog asks relay to check the
 *  credit, so it checks once the admin stops typing rather than on every key. */
const CHECK_DELAY_MS = 500

/** A check that failed for a passing reason (no answer, or a server error) is
 *  tried again after this long, up to MAX_CHECK_RETRIES times. */
const RETRY_DELAY_MS = 1500
const MAX_CHECK_RETRIES = 3

const ERROR_PREFIXES: Record<string, string> = {
  duplicate_credit: 'Already on the ledger:',
  past_month: 'Pick a date up to six months back:',
}

/** True when a failure says nothing about the credit itself: no reply, or a
 *  server error without a refusal code. Refusals wait for the admin to edit. */
function isPassingFailure(error: Error): boolean {
  if (!(error instanceof ApiError)) return true
  return !error.code && (error.status === 0 || error.status >= 500)
}

function errorMessage(error: Error): string {
  const prefix = error instanceof ApiError && error.code
  return prefix && ERROR_PREFIXES[prefix]
    ? `${ERROR_PREFIXES[prefix]} ${error.message}`
    : error.message
}

interface AddCreditDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** YYYY-MM: the month the page shows as current. */
  period: string
  /** YYYY-MM-DD: the first day relay accepts as a credit's date, from its
   *  current usage. */
  earliestCreditDate: string
}

/** Lets Careforce staff preview, then add, a purchase or compensation credit
 *  dated from the first day relay accepts, or any later day. */
export function AddCreditDialog({
  open,
  onOpenChange,
  period,
  earliestCreditDate,
}: AddCreditDialogProps) {
  const currentPeriod = currentUtcMonth().period
  // Closing mid-write would unmount the form before it can show the outcome.
  const [writing, setWriting] = useState(false)
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && writing) return
        onOpenChange(next)
      }}>
      <DialogContent showCloseButton={!writing}>
        <DialogHeader>
          <DialogTitle>Add credit</DialogTitle>
          <DialogDescription>
            A purchase records the invoice&rsquo;s minutes and total, which must
            match the organization&rsquo;s rate card; a compensation is priced
            at it. The numbers are checked as you type.
          </DialogDescription>
        </DialogHeader>
        {period === currentPeriod ? (
          <AddCreditForm
            earliestCreditDate={earliestCreditDate}
            writing={writing}
            setWriting={setWriting}
            onDone={() => {
              setWriting(false)
              onOpenChange(false)
            }}
          />
        ) : (
          <ClosedMonthNotice shown={period} current={currentPeriod} />
        )}
      </DialogContent>
    </Dialog>
  )
}

function monthName(period: string): string {
  return formatUtcMonth(`${period}-01`)
}

/** The page was loaded in a month that has since closed. A credit can be
 *  dated in the current month or a later one, and the page shows the current
 *  month once it is reloaded. */
function ClosedMonthNotice({
  shown,
  current,
}: {
  shown: string
  current: string
}) {
  return (
    <>
      <p className="text-sm">
        {monthName(shown)} has closed. Reload the page to add a credit to{' '}
        {monthName(current)}.
      </p>
      <DialogFooter>
        <Button onClick={() => window.location.reload()}>Reload page</Button>
      </DialogFooter>
    </>
  )
}

function AddCreditForm({
  earliestCreditDate,
  writing,
  setWriting,
  onDone,
}: {
  earliestCreditDate: string
  /** A write is in flight; held by the dialog, which stays open meanwhile. */
  writing: boolean
  setWriting: (writing: boolean) => void
  onDone: () => void
}) {
  const month = currentUtcMonth()
  const mutation = useAddBillingCreditMutation()
  const [creditType, setCreditType] = useState<CreditType>('purchase')
  const [minutes, setMinutes] = useState('')
  const [amount, setAmount] = useState('')
  const [creditDate, setCreditDate] = useState(month.today)
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [note, setNote] = useState('')
  const [reason, setReason] = useState('')
  const [plan, setPlan] = useState<BillingCreditPlan | null>(null)
  const [error, setError] = useState<string | null>(null)
  // A failed write's own message, kept after the check that follows it
  // passes, so a credit that did not go through never looks like it did.
  const [writeError, setWriteError] = useState<string | null>(null)
  const [recheck, setRecheck] = useState(0)
  // Every edit schedules a check, including one that leaves the credit as it
  // was (a dropped comma, a trailing space), since the edit cleared the plan.
  const [edits, setEdits] = useState(0)

  const editCount = useRef(0)
  const retries = useRef(0)

  // Any edit invalidates a preview, including one still in flight, so Add
  // credit only ever writes what was previewed.
  function edit<T>(setter: (value: T) => void) {
    return (value: T) => {
      if (writing) return
      editCount.current += 1
      setEdits(editCount.current)
      retries.current = 0
      setRecheck(0)
      setter(value)
      setPlan(null)
      setError(null)
      setWriteError(null)
    }
  }

  const trimmedNote = note.trim()
  const trimmedReason = reason.trim()
  const body: CreditRequestBody =
    creditType === 'purchase'
      ? {
          credit_type: 'purchase',
          minutes,
          amount,
          credit_date: creditDate,
          invoice_number: invoiceNumber,
          ...(trimmedNote ? { note: trimmedNote } : {}),
          dry_run: true,
        }
      : {
          credit_type: 'compensation',
          minutes,
          credit_date: creditDate,
          note: trimmedReason,
          dry_run: true,
        }

  const isValid =
    MINUTES_PATTERN.test(minutes) &&
    Number(minutes) > 0 &&
    creditDate >= earliestCreditDate &&
    (creditType === 'purchase'
      ? AMOUNT_PATTERN.test(amount) &&
        Number(amount) > 0 &&
        INVOICE_NUMBER_PATTERN.test(invoiceNumber)
      : trimmedReason.length > 0)

  // Why a filled-in field keeps the credit from being checked; an empty field
  // needs no explaining.
  const minutesHint =
    minutes && !(MINUTES_PATTERN.test(minutes) && Number(minutes) > 0)
      ? 'More than 0, with up to 4 decimal places.'
      : null
  const amountHint =
    amount && !(AMOUNT_PATTERN.test(amount) && Number(amount) > 0)
      ? 'Dollars and cents, more than 0.'
      : null
  const invoiceNumberHint =
    invoiceNumber && !INVOICE_NUMBER_PATTERN.test(invoiceNumber)
      ? '3 or more digits, or letters and digits joined by single hyphens, like INV-2041.'
      : null

  // Once every field is valid, ask relay to check the credit (a dry run) after
  // the admin stops typing; Add credit stays disabled until that check passes.
  const submitRef = useRef(submit)
  submitRef.current = submit
  const checkKey = isValid ? JSON.stringify(body) : null
  useEffect(() => {
    if (checkKey === null) return
    const request = JSON.parse(checkKey) as CreditRequestBody
    const timer = setTimeout(
      () => submitRef.current(request, () => {}),
      recheck ? RETRY_DELAY_MS : CHECK_DELAY_MS
    )
    return () => clearTimeout(timer)
  }, [checkKey, recheck, edits])

  function submit(request: CreditRequestBody, onSuccess: () => void) {
    setError(null)
    const editCountAtSubmit = editCount.current
    mutation.mutate(request, {
      onSuccess: (change) => {
        retries.current = 0
        if (change.dry_run && editCount.current === editCountAtSubmit)
          setPlan(change.plan)
        onSuccess()
      },
      onError: (err) => {
        // An answer to a check the admin has since typed past says nothing
        // about what is on screen now.
        if (editCount.current !== editCountAtSubmit) return
        setPlan(null)
        if (!request.dry_run) {
          setWriteError(errorMessage(err))
          // The write may or may not have landed; checking again shows which
          // (a landed credit comes back as already on the ledger).
          setWriting(false)
          setRecheck((n) => n + 1)
          return
        }
        setError(errorMessage(err))
        if (isPassingFailure(err) && retries.current < MAX_CHECK_RETRIES) {
          retries.current += 1
          setRecheck((n) => n + 1)
        }
      },
    })
  }

  return (
    <div className="grid gap-4">
      <RadioGroup
        disabled={writing}
        value={creditType}
        onValueChange={edit((value: string) => {
          setCreditType(value as CreditType)
          setAmount('')
        })}
        className="grid-flow-col justify-start gap-6">
        {(Object.keys(CREDIT_TYPE_ENTRY) as CreditType[]).map((type) => (
          <div key={type} className="flex items-center gap-2">
            <RadioGroupItem value={type} id={`credit-type-${type}`} />
            <Label htmlFor={`credit-type-${type}`}>
              {CREDIT_TYPE_LABELS[CREDIT_TYPE_ENTRY[type]]}
            </Label>
          </div>
        ))}
      </RadioGroup>

      <div className="grid gap-2">
        <Label htmlFor="credit-minutes">
          {creditType === 'purchase'
            ? 'Minutes (Qty on the invoice)'
            : 'Minutes'}
        </Label>
        <Input
          id="credit-minutes"
          inputMode="decimal"
          disabled={writing}
          value={minutes}
          aria-invalid={!!minutesHint}
          aria-describedby={minutesHint ? 'credit-minutes-hint' : undefined}
          onChange={(e) => {
            const value = numberInput(e.target.value)
            if (value !== null) edit(setMinutes)(value)
          }}
        />
        <FieldHint id="credit-minutes-hint">{minutesHint}</FieldHint>
      </div>

      {creditType === 'purchase' && (
        <div className="grid gap-2">
          <Label htmlFor="credit-amount">Total ($ on the invoice)</Label>
          <Input
            id="credit-amount"
            inputMode="decimal"
            disabled={writing}
            value={amount}
            aria-invalid={!!amountHint}
            aria-describedby={amountHint ? 'credit-amount-hint' : undefined}
            onChange={(e) => {
              const value = numberInput(e.target.value)
              if (value !== null) edit(setAmount)(value)
            }}
          />
          <FieldHint id="credit-amount-hint">{amountHint}</FieldHint>
        </div>
      )}

      <div className="grid gap-2">
        <Label htmlFor="credit-date">Date</Label>
        <DayField
          id="credit-date"
          value={creditDate}
          earliestDay={earliestCreditDate}
          disabled={writing}
          onChange={edit(setCreditDate)}
        />
      </div>

      {creditType === 'purchase' ? (
        <>
          <div className="grid gap-2">
            <Label htmlFor="credit-invoice-number">Invoice number</Label>
            <Input
              id="credit-invoice-number"
              disabled={writing}
              value={invoiceNumber}
              aria-invalid={!!invoiceNumberHint}
              aria-describedby={
                invoiceNumberHint ? 'credit-invoice-number-hint' : undefined
              }
              onChange={(e) => edit(setInvoiceNumber)(e.target.value)}
            />
            <FieldHint id="credit-invoice-number-hint">
              {invoiceNumberHint}
            </FieldHint>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="credit-note">Note (optional)</Label>
            <Input
              id="credit-note"
              disabled={writing}
              value={note}
              onChange={(e) => edit(setNote)(e.target.value)}
            />
          </div>
        </>
      ) : (
        <div className="grid gap-2">
          <Label htmlFor="credit-reason">Reason</Label>
          <Input
            id="credit-reason"
            disabled={writing}
            value={reason}
            onChange={(e) => edit(setReason)(e.target.value)}
          />
        </div>
      )}

      {plan && (
        <dl className="grid grid-cols-3 gap-2 rounded-md border border-border p-3 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">Minutes</dt>
            <dd>{`+${formatMinutes(Number(plan.minutes))}`}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Rate ($/min)</dt>
            <dd>{formatRate(Number(plan.minute_rate))}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Amount</dt>
            <dd>{formatCurrency(Math.abs(Number(plan.amount)))}</dd>
          </div>
        </dl>
      )}

      {plan && plan.credit_date.slice(0, 7) > month.period && (
        // The billing page opens no month after the current one.
        <p className="text-sm text-muted-foreground">
          It shows under Credits Added once{' '}
          {monthName(plan.credit_date.slice(0, 7))} begins.
        </p>
      )}

      {plan && plan.credit_date.slice(0, 7) < month.period && (
        <p className="text-sm text-muted-foreground">
          It shows under Credits Added for{' '}
          {monthName(plan.credit_date.slice(0, 7))}, and that month&rsquo;s
          balances and every later month&rsquo;s start count it.
        </p>
      )}

      {!plan && !error && mutation.isPending && (
        <p role="status" className="text-sm text-muted-foreground">
          Checking the numbers&hellip;
        </p>
      )}

      {writeError && (
        <p role="alert" className="text-sm text-error-low-contrast">
          The last attempt to add this credit failed:{' '}
          {writeError.replace(/\.?$/, '.')} The check below is current.
        </p>
      )}

      {error && (
        <p role="alert" className="text-sm text-error-low-contrast">
          {error}
        </p>
      )}

      <DialogFooter>
        <Button
          disabled={!plan || mutation.isPending || writing}
          onClick={() => {
            setWriting(true)
            setWriteError(null)
            // The write carries the checked inputs, not a token for the checked
            // plan, so relay prices it again on write. Deliberate: a rate card
            // changed between the check and the click is rare, and only
            // Careforce admins change cards or add credits. A repeated write is
            // refused by relay as duplicate_credit.
            submit({ ...body, dry_run: false }, onDone)
          }}>
          Add credit
        </Button>
      </DialogFooter>
    </div>
  )
}

function FieldHint({ id, children }: { id: string; children: string | null }) {
  if (!children) return null
  return (
    <p id={id} className="text-xs text-error-low-contrast">
      {children}
    </p>
  )
}
